import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";

// Public Verification-of-Employment signing page for the employer's officer
// (e.g. the company CFO). DELIBERATELY UNBRANDED — plain document language
// only: the signer sees the lender's own form, completes the employer block,
// and draws a signature. Nothing here references our company or products.

export default function VoeSignPage() {
  const { token = "" } = useParams();
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [clientName, setClientName] = useState("");
  const [signed, setSigned] = useState(false);
  const [signKind, setSignKind] = useState<"employer" | "applicant" | "letter">("employer");

  const [printedName, setPrintedName] = useState("");
  const [title, setTitle] = useState("");
  const [phone, setPhone] = useState("");
  const [dateStr, setDateStr] = useState(new Date().toISOString().slice(0, 10));
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const drawing = useRef(false);
  const hasInk = useRef(false);

  useEffect(() => {
    fetch(`/api/voe/${token}`)
      .then((r) => r.json())
      .then((d) => {
        if (!d.ok) { setErr(d.message || "This link is not valid."); return; }
        setClientName(d.request.client_name || "");
        setSigned(!!d.request.signed);
        setSignKind(d.request.sign_kind === "applicant" ? "applicant" : d.request.sign_kind === "letter" ? "letter" : "employer");
      })
      .catch(() => setErr("Something went wrong loading this page."))
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext("2d")!;
    ctx.lineWidth = 5; ctx.lineCap = "round"; ctx.lineJoin = "round"; ctx.strokeStyle = "#101040";
    const pos = (e: PointerEvent) => {
      const r = c.getBoundingClientRect();
      return { x: (e.clientX - r.left) * (c.width / r.width), y: (e.clientY - r.top) * (c.height / r.height) };
    };
    const down = (e: PointerEvent) => { drawing.current = true; const p = pos(e); ctx.beginPath(); ctx.moveTo(p.x, p.y); e.preventDefault(); };
    const move = (e: PointerEvent) => { if (!drawing.current) return; const p = pos(e); ctx.lineTo(p.x, p.y); ctx.stroke(); hasInk.current = true; e.preventDefault(); };
    const up = () => { drawing.current = false; };
    c.addEventListener("pointerdown", down); c.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    return () => { c.removeEventListener("pointerdown", down); c.removeEventListener("pointermove", move); window.removeEventListener("pointerup", up); };
  }, [loading, signed]);

  function clearSig() {
    const c = canvasRef.current; if (!c) return;
    c.getContext("2d")!.clearRect(0, 0, c.width, c.height);
    hasInk.current = false;
  }

  async function submit() {
    setMsg(null);
    if (signKind === "applicant" || signKind === "letter") {
      if (!printedName.trim()) { setMsg("Please type your full name."); return; }
    } else if (!printedName.trim() || !title.trim() || !phone.trim()) { setMsg("Please fill in your name, title, and phone number."); return; }
    if (!hasInk.current) { setMsg("Please draw your signature in the box."); return; }
    setBusy(true);
    try {
      // Trim the drawing to its inked bounding box (plus padding) so the
      // signature fills the form's signature field instead of shrinking with
      // the whole canvas — otherwise the strokes render hairline-thin.
      const src = canvasRef.current!;
      const sctx = src.getContext("2d")!;
      const data = sctx.getImageData(0, 0, src.width, src.height).data;
      let minX = src.width, minY = src.height, maxX = 0, maxY = 0;
      for (let y = 0; y < src.height; y++) {
        for (let x = 0; x < src.width; x++) {
          if (data[(y * src.width + x) * 4 + 3] > 10) {
            if (x < minX) minX = x; if (x > maxX) maxX = x;
            if (y < minY) minY = y; if (y > maxY) maxY = y;
          }
        }
      }
      const pad = 8;
      minX = Math.max(0, minX - pad); minY = Math.max(0, minY - pad);
      maxX = Math.min(src.width, maxX + pad); maxY = Math.min(src.height, maxY + pad);
      const out = document.createElement("canvas");
      out.width = Math.max(1, maxX - minX); out.height = Math.max(1, maxY - minY);
      out.getContext("2d")!.drawImage(src, minX, minY, out.width, out.height, 0, 0, out.width, out.height);
      const png = out.toDataURL("image/png");
      const r = await fetch(`/api/voe/${token}`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ printed_name: printedName.trim(), title: title.trim(), phone: phone.trim(), date: dateStr, signature_png: png }),
      });
      const d = await r.json();
      if (!r.ok || !d.ok) throw new Error(d.message || "Could not submit.");
      setSigned(true);
    } catch (e: any) { setMsg(e?.message || "Could not submit — please try again."); }
    setBusy(false);
  }

  const box: React.CSSProperties = { maxWidth: 640, margin: "40px auto", padding: "28px 24px", background: "#fff", color: "#111", borderRadius: 10, border: "1px solid #ddd", fontFamily: "Georgia, 'Times New Roman', serif" };
  const input: React.CSSProperties = { width: "100%", padding: "10px 12px", fontSize: 15, border: "1px solid #bbb", borderRadius: 6, marginTop: 4, boxSizing: "border-box", background: "#fff", color: "#111" };
  const label: React.CSSProperties = { display: "block", marginTop: 14, fontSize: 14, fontWeight: 600 };

  if (loading) return <div style={{ background: "#f4f4f2", minHeight: "100vh" }}><div style={box}><p>Loading…</p></div></div>;
  if (err) return <div style={{ background: "#f4f4f2", minHeight: "100vh" }}><div style={box}><h2>Link unavailable</h2><p>{err}</p></div></div>;
  if (signed) return (
    <div style={{ background: "#f4f4f2", minHeight: "100vh" }}>
      <div style={box}>
        <h2 style={{ marginTop: 0 }}>✓ Signed and submitted</h2>
        <p>Thank you. Your signed document has been recorded. Download a copy for your records below.</p>
        <p style={{ marginTop: 16 }}>
          <a href={`/api/voe/${token}/pdf`} download
             style={{ display: "inline-block", padding: "12px 22px", background: "#1a1a1a", color: "#fff", borderRadius: 8, textDecoration: "none", fontWeight: 700 }}>
            ⬇ Download signed document (PDF)
          </a>
        </p>
      </div>
    </div>
  );

  const isApplicant = signKind === "applicant";
  const isLetter = signKind === "letter";
  const heading = isLetter ? "Document Signature" : isApplicant ? "Verification of Employment — Applicant Signature" : "Verification of Employment — Employer Signature";
  return (
    <div style={{ background: "#f4f4f2", minHeight: "100vh", paddingBottom: 60 }}>
      <div style={box}>
        <h2 style={{ marginTop: 0 }}>{heading}</h2>
        <p style={{ fontSize: 15, lineHeight: 1.55 }}>
          {isLetter ? (
            <>The attached documents are ready for your signature. Please <strong>review them</strong>, then type your
            name and sign once below — your signature is applied to each page.</>
          ) : isApplicant ? (
            <>Your Request for Verification of Employment needs <strong>your consent signature</strong> (Part I, Box 6).
            Everything else is already completed — just review the form, type your name, and sign.</>
          ) : (
            <>A Request for Verification of Employment{clientName ? <> regarding <strong>{clientName}</strong></> : null} requires
            completion of the employer's <em>Authorized Signature</em> section (Part IV).
            The employment details have already been entered — please review the form, then confirm your
            information below and sign.</>
          )}
        </p>
        {isLetter && (
          <p style={{ fontSize: 15 }}>
            📄 <a href={`/api/voe/${token}/pdf`} download><strong>Download the documents (PDF)</strong></a> to review or keep a copy.
          </p>
        )}
        <p style={{ fontSize: 15 }}>
          📄 <a href={`/api/voe/${token}/pdf`} target="_blank" rel="noreferrer"><strong>Review the completed form (PDF)</strong></a> before signing.
        </p>

        <label style={label}>{(isApplicant || isLetter) ? "Type your full name *" : "Print your full name (Item 24) *"}
          <input style={input} value={printedName} onChange={(e) => setPrintedName(e.target.value)} placeholder="First Last" autoComplete="name" /></label>
        {!isApplicant && !isLetter && (<>
          <label style={label}>Your title (Item 23) *
            <input style={input} value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Chief Financial Officer" /></label>
          <label style={label}>Phone number (Item 25) *
            <input style={input} value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="(555) 555-5555" type="tel" autoComplete="tel" /></label>
          <label style={label}>Date (Item 26)
            <input style={input} value={dateStr} onChange={(e) => setDateStr(e.target.value)} type="date" /></label>
        </>)}

        <label style={label}>{isLetter ? "Signature — draw with your finger or mouse *" : isApplicant ? "Signature (Box 6) — draw with your finger or mouse *" : "Signature (Item 22) — draw with your finger or mouse *"}</label>
        <canvas ref={canvasRef} width={1140} height={260}
          style={{ width: "100%", height: 130, border: "1.5px dashed #999", borderRadius: 6, background: "#fff", touchAction: "none", marginTop: 4 }} />
        <button type="button" onClick={clearSig}
          style={{ marginTop: 6, padding: "6px 14px", fontSize: 13, border: "1px solid #bbb", borderRadius: 6, background: "#f7f7f7", cursor: "pointer" }}>
          Clear signature
        </button>

        <p style={{ fontSize: 12.5, color: "#444", lineHeight: 1.5, marginTop: 16 }}>
          {isApplicant
            ? "By clicking “Sign & Submit,” you authorize the lender to contact the employer listed on the form to verify the requested information, as described in Part I. Your signature and a record of this submission will be applied to the document."
            : "By clicking “Sign & Submit,” you certify that you are authorized to verify employment for this employer and that the information on the form is true and correct to the best of your knowledge. Your signature, the date, and a record of this submission will be applied to the document."}
        </p>
        {msg && <p style={{ color: "#b00020", fontSize: 14 }} role="alert">{msg}</p>}
        <button type="button" onClick={submit} disabled={busy}
          style={{ width: "100%", marginTop: 8, padding: "13px 0", fontSize: 16, fontWeight: 700, color: "#fff", background: busy ? "#666" : "#1a1a1a", border: "none", borderRadius: 8, cursor: "pointer" }}>
          {busy ? "Submitting…" : "Sign & Submit"}
        </button>
      </div>
    </div>
  );
}
